import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Atom, Calculator, Beaker, Check } from 'lucide-react';
import content from '../../data/content.json';

const Subjects = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="subjects" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Subjects We Offer
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Comprehensive tutoring in Science and Mathematics with NCERT-focused, concept-driven learning
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1560785472-2f186f554644"
                  alt="Mathematics Education"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-bold text-white mb-2">{content.subjects.mathematics.title}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                  {content.subjects.mathematics.description}
                </p>
                <ul className="space-y-2">
                  {content.subjects.mathematics.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1602052577122-f73b9710adba"
                  alt="Science Education"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-bold text-white mb-2">{content.subjects.science.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {content.subjects.science.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                  {content.subjects.science.description}
                </p>
                <ul className="space-y-2">
                  {content.subjects.science.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md text-center overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb"
                  alt="Mathematics"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Mathematics</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Algebra, Geometry, Calculus</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md text-center overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa"
                  alt="Physics"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Physics</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Mechanics, Optics, Thermodynamics</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md text-center overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1603126857599-f6e157fa2fe6"
                  alt="Chemistry"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Chemistry</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Organic, Inorganic, Physical</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md text-center overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1576086213369-97a306d36557"
                  alt="Biology"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Biology</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Botany, Zoology, Anatomy</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{content.subjects.other.title}</h3>
            <div className="flex flex-wrap gap-3">
              {content.subjects.other.subjects.map((subject, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium shadow-sm"
                >
                  {subject}
                </span>
              ))}
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium mt-6">
              Languages we teach in: English, Hindi, Telugu
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Subjects;
